/**
 * ALTIORA · AdminGeoConfig — Localisation par défaut de la plateforme.
 *
 * Interface admin (founder / super_admin / admin) pour piloter la valeur
 * fallback utilisée par les pages publiques et le hook `useUserLocation`
 * quand la géolocalisation navigateur est indisponible.
 *
 * Vision : plus aucune ville codée en dur — ALTIORA fonctionne mondialement.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Save, RefreshCw } from "lucide-react";
import {
  PageShell, PageHeader, Card, Section, PrimaryButton, GhostButton,
  FormField, Input, Alert, Loader,
  NAVY, CHAMPAGNE, IVORY, SLATE,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";
import { api } from "@/lib/api";

export default function AdminGeoConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    label: "", city: "", region: "", country_code: "",
    lat: "", lng: "",
  });
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/geo-config/default-location");
      setForm({
        label: data.label || "",
        city: data.city || "",
        region: data.region || "",
        country_code: data.country_code || "",
        lat: String(data.lat ?? ""),
        lng: String(data.lng ?? ""),
      });
    } catch {
      toast.error("Impossible de charger la localisation par défaut.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const save = async () => {
    const lat = parseFloat(form.lat);
    const lng = parseFloat(form.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      toast.error("Latitude et longitude doivent être des nombres.");
      return;
    }
    if (!form.label || !form.city || !form.country_code) {
      toast.error("Libellé, ville et code pays sont requis.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/geo-config/admin/default-location", {
        label: form.label,
        city: form.city,
        region: form.region || null,
        country_code: form.country_code.toUpperCase(),
        lat, lng,
      });
      toast.success("Localisation par défaut mise à jour.");
      setUpdatedAt(data?.location?.updated_at || new Date().toISOString());
      // Invalide le cache local pour que le prochain chargement reflète la nouvelle valeur
      try { localStorage.removeItem("altiora_user_location_v1"); } catch { /* noop */ }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Enregistrement impossible.");
    } finally { setSaving(false); }
  };

  return (
    <PageShell testid="admin-geo-root">
      <TopBar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <PageHeader
          eyebrow="ALTIORA · Administration"
          title="Localisation par défaut de la plateforme"
          subtitle="Valeur fallback utilisée lorsque la géolocalisation du navigateur est indisponible. Aucune ville n'est codée en dur — modifiez ici pour tout le monde."
          testid="admin-geo-header"
        />

        {loading ? (
          <div className="mt-8 flex items-center justify-center py-16">
            <Loader size={24} />
          </div>
        ) : (
          <>
            <Alert variant="info" title="Portée du réglage" testid="admin-geo-alert">
              Cette localisation est visible publiquement (endpoint <code>/api/geo/default-location</code>)
              et sert de fallback sur le Landing, la page Login et les hooks internes.
            </Alert>

            <Card className="p-6 mt-4">
              <Section icon={MapPin} title="Localisation">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <FormField label="Libellé affiché" required testid="admin-geo-ff-label">
                    {(a) => <Input testid="admin-geo-input-label" placeholder="Lévis, Québec"
                                   value={form.label} onChange={set("label")} {...a} />}
                  </FormField>
                  <FormField label="Ville" required testid="admin-geo-ff-city">
                    {(a) => <Input testid="admin-geo-input-city" placeholder="Lévis"
                                   value={form.city} onChange={set("city")} {...a} />}
                  </FormField>
                  <FormField label="Région / État" testid="admin-geo-ff-region">
                    {(a) => <Input testid="admin-geo-input-region" placeholder="Québec"
                                   value={form.region} onChange={set("region")} {...a} />}
                  </FormField>
                  <FormField label="Code pays (ISO-2)" required testid="admin-geo-ff-cc">
                    {(a) => <Input testid="admin-geo-input-cc" placeholder="CA" maxLength={3}
                                   value={form.country_code} onChange={set("country_code")} {...a} />}
                  </FormField>
                  <FormField label="Latitude" required testid="admin-geo-ff-lat">
                    {(a) => <Input testid="admin-geo-input-lat" placeholder="46.8033" inputMode="decimal"
                                   value={form.lat} onChange={set("lat")} {...a} />}
                  </FormField>
                  <FormField label="Longitude" required testid="admin-geo-ff-lng">
                    {(a) => <Input testid="admin-geo-input-lng" placeholder="-71.1779" inputMode="decimal"
                                   value={form.lng} onChange={set("lng")} {...a} />}
                  </FormField>
                </div>

                <div className="mt-6 flex items-center gap-3">
                  <PrimaryButton onClick={save} disabled={saving} testid="admin-geo-save">
                    <Save size={13} /> {saving ? "Enregistrement…" : "Enregistrer"}
                  </PrimaryButton>
                  <GhostButton onClick={load} testid="admin-geo-refresh">
                    <RefreshCw size={13} /> Recharger
                  </GhostButton>
                  {updatedAt && (
                    <span className="text-[11px] font-mono uppercase tracking-widest"
                          style={{ color: SLATE }}>
                      MAJ · {new Date(updatedAt).toLocaleString("fr-CA")}
                    </span>
                  )}
                </div>
              </Section>
            </Card>

            <Card className="p-6 mt-4">
              <Section title="Astuce — coordonnées">
                <p className="text-[13px]" style={{ color: NAVY }}>
                  Trouvez les coordonnées d'une ville sur <a
                    href="https://www.openstreetmap.org/" target="_blank" rel="noopener noreferrer"
                    style={{ color: CHAMPAGNE, textDecoration: "underline" }}>OpenStreetMap</a> —
                  clic droit → « Afficher l'adresse ». Les coordonnées apparaissent en haut à gauche.
                </p>
              </Section>
            </Card>
          </>
        )}
      </div>
    </PageShell>
  );
}
